import { Record } from '../../../../models/record';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { CloseCitationModal, GetCitationStyles, OpenCitationModal, SetSelectedCitationStyle } from './citation.actions';
import { patch } from '@ngxs/store/operators';
import { LoadingModel } from '../../loading.model';
import { CitationService } from '../../../services/dialogs/citation/citation.service';
import { finalize } from 'rxjs/operators';
import { tap } from 'rxjs';
import { CitationStyle } from '../../../services/dialogs/citation/citation.model';

export type CitationStateModel = LoadingModel & {
  isOpen: boolean,
  record: Record | null,
  styles: CitationStyle[],
  selectedStyle: CitationStyle | undefined,
};

export const initialState: CitationStateModel = {
  isLoading: false,
  isOpen: false,
  record: null,
  styles: [],
  selectedStyle: undefined,
};

@State<CitationStateModel>({
  name: 'citation',
  defaults: initialState,
})
@Injectable()
export class CitationState {
  private readonly citationService = inject(CitationService);

  @Selector() static isLoading(state: CitationStateModel) {
    return state.isLoading;
  }

  @Selector()
  static isOpen(state: CitationStateModel) {
    return state.isOpen;
  }

  @Selector()
  static record(state: CitationStateModel) {
    return state.record;
  }

  @Selector()
  static styles(state: CitationStateModel) {
    return state.styles;
  }

  @Selector()
  static selectedStyle(state: CitationStateModel) {
    return state.selectedStyle;
  }

  @Action(OpenCitationModal)
  openCitationModal(context: StateContext<CitationStateModel>, { record }: OpenCitationModal) {
    context.setState(patch({ isOpen: true, record }));
    context.dispatch(new GetCitationStyles(record.id));
  }

  @Action(CloseCitationModal)
  closeCitationModal(context: StateContext<CitationStateModel>) {
    context.setState(initialState);
  }

  @Action(GetCitationStyles)
  getCitationStyles(context: StateContext<CitationStateModel>, { recordId }: GetCitationStyles) {
    context.setState(patch({ isLoading: true }));

    return this.citationService.getCitationStyles(recordId).pipe(
      tap((styles) => {
        context.setState(patch({ styles }))
        context.dispatch(new SetSelectedCitationStyle(styles[0]));
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false }));
      })
    );
  }

  @Action(SetSelectedCitationStyle)
  setSelectedCitationStyle(context: StateContext<CitationStateModel>, { style }: SetSelectedCitationStyle) {
    context.setState(patch({ selectedStyle: style }));
  }
}
